import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { UserRole, type PublicUserDto } from "@gryzonie/shared";
import { api } from "../lib/api";

export function AdminPage() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery<PublicUserDto[]>({
    queryKey: ["users"],
    queryFn: () => api<PublicUserDto[]>("/users"),
  });

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    email: "",
    displayName: "",
    password: "",
    role: UserRole.Viewer as "admin" | "viewer",
  });

  const createUser = useMutation({
    mutationFn: (payload: typeof form) =>
      api<PublicUserDto>("/users", { method: "POST", json: payload }),
    onSuccess: () => {
      toast.success("Użytkownik utworzony");
      setForm({ email: "", displayName: "", password: "", role: UserRole.Viewer });
      setCreating(false);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api<PublicUserDto>(`/users/${id}`, { method: "PATCH", json: { isActive } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["users"] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const removeUser = useMutation({
    mutationFn: (id: string) => api(`/users/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Użytkownik usunięty");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const resetPwd = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api(`/users/${id}/reset-password`, { method: "POST", json: { password } }),
    onSuccess: () => toast.success("Hasło zaktualizowane"),
    onError: (e: Error) => toast.error(e.message),
  });

  const onCreate = (e: FormEvent) => {
    e.preventDefault();
    createUser.mutate(form);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Administracja użytkownikami</h1>
        <button
          onClick={() => setCreating((v) => !v)}
          className="rounded bg-primary px-3 py-1.5 text-sm font-semibold text-crust hover:opacity-90"
        >
          {creating ? "Anuluj" : "Nowy użytkownik"}
        </button>
      </div>

      {creating && (
        <form
          onSubmit={onCreate}
          className="grid gap-3 rounded border border-surface0 bg-mantle p-4 sm:grid-cols-2"
        >
          <input
            type="email"
            placeholder="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="rounded border border-surface1 bg-surface0 px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <input
            placeholder="Nazwa wyświetlana"
            required
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            className="rounded border border-surface1 bg-surface0 px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <input
            type="password"
            placeholder="Hasło (min. 8, A-z 0-9)"
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="rounded border border-surface1 bg-surface0 px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <select
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as "admin" | "viewer" })}
            className="rounded border border-surface1 bg-surface0 px-3 py-2 text-sm outline-none focus:border-primary"
          >
            <option value={UserRole.Viewer}>Viewer</option>
            <option value={UserRole.Admin}>Admin</option>
          </select>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={createUser.isPending}
              className="rounded bg-success px-4 py-2 text-sm font-semibold text-crust hover:opacity-90 disabled:opacity-50"
            >
              {createUser.isPending ? "Tworzenie…" : "Utwórz"}
            </button>
          </div>
        </form>
      )}

      <div className="overflow-auto rounded border border-surface0 bg-mantle">
        <table className="min-w-full text-sm">
          <thead className="bg-crust text-left text-xs uppercase tracking-wider text-subtext0">
            <tr>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Nazwa</th>
              <th className="px-3 py-2">Rola</th>
              <th className="px-3 py-2">Aktywny</th>
              <th className="px-3 py-2">Ostatnie logowanie</th>
              <th className="px-3 py-2">Akcje</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-subtext0">
                  Wczytywanie…
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} className="border-t border-surface0 hover:bg-surface0/40">
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">{u.displayName}</td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        u.role === UserRole.Admin
                          ? "rounded bg-accent/20 px-2 py-0.5 text-accent"
                          : "rounded bg-surface0 px-2 py-0.5 text-subtext1"
                      }
                    >
                      {u.role}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleActive.mutate({ id: u.id, isActive: !u.isActive })}
                      className={`rounded px-2 py-0.5 text-xs ${
                        u.isActive
                          ? "bg-success/20 text-success"
                          : "bg-danger/20 text-danger"
                      }`}
                    >
                      {u.isActive ? "Aktywny" : "Nieaktywny"}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-subtext1">
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString("pl-PL") : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const pwd = window.prompt("Nowe hasło dla " + u.email);
                          if (pwd) resetPwd.mutate({ id: u.id, password: pwd });
                        }}
                        className="rounded border border-surface1 bg-surface0 px-2 py-0.5 text-xs hover:bg-surface1"
                      >
                        Reset hasła
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm(`Usunąć użytkownika ${u.email}?`)) {
                            removeUser.mutate(u.id);
                          }
                        }}
                        className="rounded border border-danger/40 bg-danger/10 px-2 py-0.5 text-xs text-danger hover:bg-danger/20"
                      >
                        Usuń
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
